<template>
  <div>
    <div :name="formVersion" v-show="isLoaded" class="dynform">
      <BaseFormStore
        :subform-grid-view="subformGridView"
        :username-hidden="usernameHidden"
        :group-auto-collapse="groupAutoCollapse"
        :hidden-form-number="hiddenFormNumber"
      >
        <div slot="buttons" v-show="false"></div>
      </BaseFormStore>
    </div>
    <div v-show="!isLoaded">
      <Loader />
    </div>
  </div>
</template>
<script>
import { createForm } from '../../form'
import formStore from '../../store/modules/form/index'
import { mapState } from 'vuex'
import Vue from 'vue'
import url from 'url'
import querystring from 'querystring'
import Loader from '../Loader'
import { get } from 'lodash'

/**
 * @class FormDataVersionView
 *
 * @description This component renders one dataversion of a Form on the comparison
 */
export default {
  name: 'FormDataVersionView',
  instanceStore: {
    name: 'formStore',
    module: formStore,
  },
  props: {
    idForm: {
      type: String,
      default: '',
    },
    externalParams: {
      type: Object,
      default: () => ({}),
    },
    groupAutoCollapse: {
      type: Boolean,
      default: false,
    },
    subformGridView: {
      type: Boolean,
      default: true,
    },
    usernameHidden: {
      type: Boolean,
      default: false,
    },
    readOnly: {
      type: Boolean,
      default: true,
    },
    version: {
      type: Object,
      default: () => ({}),
    },
    url: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      mainTable: null,
      formUrl: '',
      isLoading: false,
      hiddenFormNumber: null,
      idFormDataVersion: null,
      isLoaded: false,
    }
  },
  created() {
    const urlParsed = url.parse(this.url)
    const params = querystring.parse(urlParsed.query)
    this.idFormDataVersion = this.idForm || params.id || false
    if (!this.idFormDataVersion) {
      throw 'no form data version'
    }

    if (!this.isLoaded) this.init()
  },
  destroyed() {
    this.$store.unregisterModule([this.$storeNamespace])
    delete window.Fields[this.$storeNamespace]
  },
  methods: {
    async init() {
      const { formStructure, newData, version, idMaintable, idForm, id } = await this.getDataVersion()
      const refresh = newData.REFRESH
      const forms = Object.values(formStructure.forms)
      const actualForm = forms[0]
      const formData = createForm(forms, refresh)

      this.$commit('FORM_INIT', {
        idForm: idForm,
        idMainTable: idMaintable,
        externalParams: actualForm.externalParams,
      })
      this.$commit('FORM_FETCH_STRUCTURE', formStructure)
      this.$commit('FORM_FETCH_DATA', refresh)
      this.$dispatch('formLoad', { formData })
      this.$commit('FORM_SET_READONLY', true)
      this.$commit('FORM_SET_VERSION', version)

      this.isLoaded = true
      this.collectValues()
    },
    async getDataVersion() {
      const where = {
        id: this.idFormDataVersion,
      }

      const parameters = { filter: JSON.stringify({ where }) }
      const response = await Vue.prototype.$client
        .execute({
          operationId: 'FormDataVersions_findOne',
          parameters,
        })
        .catch((e) => {
          throw 'not valid form data version'
        })
      return response.body
    },
    collectValues() {
      let values = {}
      if (!get(this, 'form.pages')) {
        return
      }
      for (let page of this.form.pages) {
        if (page)
          for (let group of page.groups) {
            if (group)
              for (let row of group.rows) {
                if (row)
                  for (let item of row) {
                    if (item) {
                      let fieldType = item.type
                      if (get(item, 'subForm.id')) {
                        const subFormName = item.subForm.name
                        values[subFormName] = {
                          id: item.subForm.id,
                          class: 'equals',
                          type: 'subform',
                          value: [],
                        }
                        if (get(item, 'subForm.rows').length)
                          for (let rowId in item.subForm.rows) {
                            const subFormRow = item.subForm.rows[rowId].items[1]
                            const fieldId = item.subForm.rows[rowId].items[0].value
                            const fieldName = `${subFormRow.name}[${rowId}]`
                            fieldType =
                              subFormRow.type === 'richtext' && typeof subFormRow.formattedText === 'undefined'
                                ? 'textarea'
                                : subFormRow.type
                            const subFormField = Object.assign(
                              {},
                              {
                                value: subFormRow.value,
                                type: fieldType,
                                id: fieldId,
                                fieldName,
                              },
                            )
                            values[subFormName].value.push(subFormField)
                          }
                      } else {
                        if (item.type === 'richtext' && typeof item.formattedText === 'undefined') {
                          fieldType = 'textarea'
                        }
                        values[item.name] = Object.assign({}, { value: item.value, type: fieldType, id: item.id })
                      }
                    }
                  }
              }
          }
      }
      const data = {
        previous: this.version.previous,
        values,
      }
      this.$emit('compare', data)
    },
    getFieldSelector({ key, field, sfField = false }) {
      switch (field.type) {
        case 'gps':
          return `[name*="${key}"]`
        case 'checkbox':
        case 'checkboxlist':
        case 'radiobuttonlist':
          return `[id*="${field.id}"]`
        case 'date':
        case 'time':
        case 'datetime':
        case 'datetime-local':
          return `[id*="${field.id}"] input`
        case 'textarea':
          return `[id*="${field.id}"] textarea`
        case 'richtext':
          return `[id*="${field.id}"] .fr-wrapper`
        case 'wpaint':
          return `[id*="${field.id}"] .doto-paint`
        case 'sqlchoosen':
          return `[name="${key}"] ~ div.data-grid`
        case 'tags':
          return `[id*="${field.id}"] span.tags-readonly-pill`
        case 'text':
          return sfField ? `[name="${field.fieldName}"]` : `[name="${key}"]`
        default:
          return `[name="${key}"]`
      }
    },
    markElements(fieldSelector, field) {
      let elements = document.querySelectorAll(`[name="${this.formVersion}"] ${fieldSelector}`)
      if (elements.length) {
        Array.from(elements).forEach((el) => {
          if (field.hasOwnProperty('class')) {
            if (['range', 'rating'].includes(field.type)) el = el.parentElement
            el.classList.add(field.class)
          }
        })
      }
    },
    markChanges(changes) {
      this.$nextTick(() => {
        if (!changes) {
          return
        }
        Object.keys(changes).forEach((key) => {
          const field = changes[key]
          let fieldSelector = this.getFieldSelector({ key, field })
          if (field.type === 'subform') {
            for (let sfField of field.value) {
              fieldSelector = this.getFieldSelector({
                key,
                field: sfField,
                sfField: true,
              })
              this.markElements(fieldSelector, sfField)
            }
          } else {
            this.markElements(fieldSelector, field)
          }
        })
      })
    },
  },
  computed: {
    ...mapState('popup', ['isOpened']),
    formVersion() {
      return this.version.previous ? 'previousFormVersion' : 'currentFormVersion'
    },
    form() {
      return this.$get('form')
    },
    formStructure() {
      return this.$get('formStructure')
    },
    dropdownVisible() {
      return Object.keys(this.getButtons).length > 1
    },
  },
  watch: {
    isLoaded: {
      handler() {
        this.collectValues()
      },
    },
  },
  components: {
    Loader,
    BaseFormStore: () => import(/* webpackMode: "eager" */ '../form/BaseFormStore'),
  },
}
</script>
<style lang="scss" scoped>
@import '../../assets/sass/components/_dynform.scss';
</style>
<style lang="scss">
.removed,
.disabled .removed,
.readonly .removed {
  border: $red 2px solid !important;
  background: rgba(254, 54, 92, 0.5) !important;
}
.modified,
.disabled .modified,
.readonly .modified {
  border: orange 2px solid !important;
  background: rgba(255, 196, 107, 0.5) !important;
}
.added,
.disabled .added,
.readonly .added {
  border: $green 2px solid !important;
  background: rgba(45, 206, 137, 0.5) !important;
}
</style>
